import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { useRewardModeration } from '../api/useRewardModeration';

const MIN_REJECT_REASON = 5;

/**
 * Reward moderation queue (SSA-818 "Deals" panel, deal→reward rename). Lists
 * custom rewards awaiting moderation and lets an admin approve or reject
 * (reject requires a reason recorded on the audit row).
 */
export function RewardModerationPanel({ env }: { env: Env }) {
  const { items, loading, error, actionError, approve, reject, refetch } = useRewardModeration(env);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const doApprove = async (id: string) => {
    setBusyId(id);
    await approve(id);
    setBusyId(null);
  };

  const doReject = async (id: string) => {
    if (rejectReason.trim().length < MIN_REJECT_REASON) return;
    setBusyId(id);
    const ok = await reject(id, rejectReason.trim());
    setBusyId(null);
    if (ok) {
      setRejectTarget(null);
      setRejectReason('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">
          {loading ? 'Loading…' : `${items.length} reward${items.length === 1 ? '' : 's'} pending moderation`}
        </p>
        <button onClick={refetch} className="text-xs text-violet-400 hover:text-violet-300">
          Refresh
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
      {actionError && <ErrorBanner message={actionError} />}
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <p className="text-sm text-gray-500 py-8 text-center">Nothing awaiting moderation. 🎉</p>
      )}

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm text-white">{r.description ?? '(no description)'}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {r.venue_name ?? 'Unknown venue'}
                  {r.coins_cost != null ? ` · ${r.coins_cost.toLocaleString()} coins` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => doApprove(r.id)}
                  disabled={busyId === r.id}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
                >
                  Approve
                </button>
                <button
                  onClick={() => {
                    setRejectTarget(rejectTarget === r.id ? null : r.id);
                    setRejectReason('');
                  }}
                  disabled={busyId === r.id}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-950/60 border border-red-800/50 text-red-300 hover:bg-red-900/50 disabled:opacity-40"
                >
                  Reject
                </button>
              </div>
            </div>

            {rejectTarget === r.id && (
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={`Reject reason (min ${MIN_REJECT_REASON} chars)`}
                  className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
                />
                <button
                  onClick={() => doReject(r.id)}
                  disabled={busyId === r.id || rejectReason.trim().length < MIN_REJECT_REASON}
                  className="px-3 py-2 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-500 disabled:opacity-40"
                >
                  Confirm reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
